import React from "react"
import { GitMerge, Crown, AlertTriangle } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "src/components/ui/dialog"
import { Button } from "src/components/ui/button"
import { Badge } from "src/components/ui/badge"
import { cn } from "src/utils/cn"
import { LeadSourceBadge, SOURCE_CONFIG } from "./LeadSourceBadge"
import { LeadStatusBadge } from "./LeadStatusBadge"

function formatDate(iso) {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
}

// Distinct source labels across the leads about to be merged — the attribution
// the surviving primary will carry.
function unionSources(leads) {
  const seen = new Map()
  leads.forEach((l) => {
    ;(l.sources?.length ? l.sources : [{ source: l.source }]).forEach((s) => {
      if (s?.source && !seen.has(s.source)) seen.set(s.source, s)
    })
  })
  return [...seen.values()]
}

// ── Merge dialog ──────────────────────────────────────────────────────────────
// Given a duplicate cluster, lets the user choose which lead survives as the
// "primary" and folds the rest into it. Shows a live preview of the combined
// source attribution so the "3 sources → 1 lead" outcome is explicit.
export function MergeLeadsDialog({ open, cluster, onClose, onConfirm, isMerging, error }) {
  const leads = cluster?.leads ?? []
  const [primaryId, setPrimaryId] = React.useState(null)

  // Default the primary to the server's suggestion (earliest-created) each time
  // a new cluster is opened.
  React.useEffect(() => {
    if (cluster) setPrimaryId(cluster.suggested_primary_id ?? cluster.leads?.[0]?.id ?? null)
  }, [cluster])

  if (!cluster) return null

  const duplicateIds = leads.map((l) => l.id).filter((id) => id !== primaryId)
  const mergedSources = unionSources(leads)

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-slate-700" />
            Merge duplicate leads
          </DialogTitle>
          <DialogDescription>
            Same buyer captured across {leads.length} records. Pick the lead to keep — the others
            fold into it and all their notes, deals, tasks &amp; source history move over.
          </DialogDescription>
        </DialogHeader>

        {/* Lead picker */}
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {leads.map((lead) => {
            const isPrimary = lead.id === primaryId
            return (
              <Button
                key={lead.id}
                type="button"
                variant="outline"
                onClick={() => setPrimaryId(lead.id)}
                className={cn(
                  "block h-auto w-full whitespace-normal rounded-lg border p-3 text-left font-normal transition-colors",
                  isPrimary
                    ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900 hover:bg-slate-50"
                    : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 truncate">{lead.name}</span>
                      {isPrimary && (
                        <Badge className="gap-1 bg-slate-900 text-white">
                          <Crown className="h-3 w-3" /> Primary
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {lead.phone || "no phone"} · {lead.email || "no email"} · added {formatDate(lead.created_at)}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <LeadStatusBadge status={lead.status} />
                    <LeadSourceBadge source={lead.source} />
                  </div>
                </div>
              </Button>
            )
          })}
        </div>

        {/* Combined attribution preview */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <p className="text-xs font-medium text-slate-600 mb-2">
            Combined source attribution on the surviving lead
          </p>
          <div className="flex flex-wrap gap-1.5">
            {mergedSources.length > 0 ? (
              mergedSources.map((s) => (
                <LeadSourceBadge key={s.source} source={s.source} />
              ))
            ) : (
              <span className="text-xs text-slate-400">No source data</span>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md bg-red-50 p-2.5 text-xs text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{Array.isArray(error) ? error.join(", ") : String(error)}</span>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isMerging}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm({ primaryId, duplicateIds })}
            disabled={isMerging || duplicateIds.length === 0}
            className="gap-2"
          >
            <GitMerge className="h-4 w-4" />
            {isMerging ? "Merging…" : `Merge ${duplicateIds.length} into primary`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
